import { useEffect, useRef, useState } from 'react'
import { ChevronUp } from 'lucide-react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

/**
 * Botón "Volver arriba" premium para artículos de ayuda.
 *
 * Posicionado en el centro inferior del viewport (no interfiere con el chat).
 * Aparece con animación cuando el usuario baja más de `threshold` píxeles.
 * Usa createPortal para escapar del overflow:hidden del layout de Tempora.
 */
interface ScrollToTopProps {
    /** ID del elemento scrollable. Default: 'main-scroll' */
    scrollContainerId?: string
    /** Píxeles de scroll antes de que aparezca el botón. Default: 400 */
    threshold?: number
}

export function ScrollToTop({
    scrollContainerId = 'main-scroll',
    threshold = 400,
}: ScrollToTopProps) {
    const { t } = useTranslation()
    const [visible, setVisible] = useState(false)
    const containerRef = useRef<HTMLElement | null>(null)

    useEffect(() => {
        const el = document.getElementById(scrollContainerId)
        containerRef.current = el
        if (!el) return

        const onScroll = () => setVisible(el.scrollTop > threshold)
        el.addEventListener('scroll', onScroll, { passive: true })
        return () => el.removeEventListener('scroll', onScroll)
    }, [scrollContainerId, threshold])

    const scrollToTop = () => {
        containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    }

    return createPortal(
        <button
            onClick={scrollToTop}
            aria-label={t('sidebar.backToTop')}
            style={{
                // Gradiente premium usando los tokens HSL de Tempora
                background: 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.75) 100%)',
                // Glow exterior con el color primario
                boxShadow: visible
                    ? '0 8px 32px -4px hsl(var(--primary) / 0.55), 0 2px 8px -2px hsl(var(--primary) / 0.35), inset 0 1px 0 hsl(0 0% 100% / 0.25)'
                    : 'none',
            }}
            className={[
                // Centrado horizontalmente en el viewport
                // Mobile: bottom-24 para librar el MobileNav. Desktop: bottom-10
                'fixed bottom-24 md:bottom-10 left-1/2 -translate-x-1/2',
                // Forma pill alargada
                'px-5 py-2.5 rounded-full',
                // Texto del botón
                'flex items-center gap-2',
                'text-sm font-semibold tracking-wide text-white',
                // Línea reflectante superior (glassmorphism leve)
                'border border-white/20',
                // Backdrop blur
                'backdrop-blur-md',
                // Z-index alto para estar sobre MobileNav si es necesario
                'z-[60]',
                // Transición de visibilidad + transformación
                'transition-all duration-500 cubic-bezier(0.4, 0, 0.2, 1)',
                // Hover: brightness + scale
                'hover:brightness-110 hover:scale-105',
                'hover:-translate-y-0.5 hover:-translate-x-1/2',
                // Active: feedback táctil
                'active:scale-95 active:-translate-x-1/2',
                // Visibilidad animada: fade + slide desde abajo
                visible
                    ? 'opacity-100 translate-y-0 pointer-events-auto scale-100'
                    : 'opacity-0 translate-y-8 pointer-events-none scale-90',
            ].join(' ')}
        >
            <div className="relative flex items-center gap-2">
                {/* Efecto de pulso sutil en el icono */}
                <ChevronUp className={cn(
                    "h-4 w-4 shrink-0 transition-transform duration-300",
                    visible && "animate-bounce"
                )} style={{ animationDuration: '2s' }} strokeWidth={2.5} />
                <span>{t('sidebar.backToTop')}</span>

                {/* Reflejo premium adicional */}
                <div className="absolute inset-0 bg-gradient-to-t from-white/0 to-white/10 rounded-full pointer-events-none" />
            </div>
        </button>,
        document.body
    )
}

// Helper para clases condicionales si no está disponible cn de lib/utils
function cn(...classes: (string | boolean | undefined | null)[]) {
    return classes.filter(Boolean).join(' ')
}
